export const programmes = [
  // ── PILLAR 1: NUTRITION & HEALTH ─────────────────────────────────────────
  {
    id: "feeding",
    name: "Feeding Programme",
    slug: "feeding-programme",
    category: "nutrition",
    pillar: "Nutrition & Health",
    shortDescription:
      "We cook and serve nutritious meals to vulnerable children and families three times a week across our centres, serving over 14,000 meals every year.",
    fullDescription: `The Feeding Programme is the heartbeat of the Food and Nutrition Foundation. Founded in 2008, it began as a simple act of compassion — cooking a pot of food for the children who had none. Today, it has grown into a structured, community-rooted operation that serves over 14,000 meals per year across our centres in Zambia, Malawi and South Africa.

Hunger is not an abstract statistic. It is a child who arrives at school unable to concentrate. It is a mother who sends her children to bed with empty stomachs because market earnings ran dry. It is a grandmother who feeds five orphaned grandchildren with whatever she can piece together.

Our dedicated kitchen team cooks nourishing meals three times a week. Children collect meals from the Centre and bring food home to share with siblings. This one act — a warm meal — has a ripple effect that touches the entire household. Children learn better, are healthier and don't get sick as often. Mothers leave for work knowing their children have eaten. Mental health improves when the constant weight of hunger is lifted.

Our goal remains unchanged: to feed the poor and vulnerable across our communities.`,
    whatIsIncluded: [
      "Nutritious cooked meals served three times per week across all centres",
      "Meal collection point for children to take home to siblings",
      "Community kitchen operations across Zambia, Malawi and South Africa",
      "Seasonal menu planning based on local produce availability",
    ],
    impactStatement:
      "14,000+ meals served every year to children and families across Southern Africa",
    donationNudge: "R100 feeds a child for one week.",
    image: "/images/gallery/eating-child-01.jpg",
    heroImage: "/images/gallery/eating-child-01.jpg",
  },
  {
    id: "nutrition",
    name: "Nutrition & Health Education",
    slug: "nutrition-education",
    category: "nutrition",
    pillar: "Nutrition & Health",
    shortDescription:
      "We teach pregnant and lactating mothers, and women with young children, how to nourish themselves and their babies — through practical workshops including psychosocial support.",
    fullDescription: `A well-nourished mother raises a well-nourished child. In communities where misinformation, poverty, and limited healthcare access collide, the basics of nutrition can be tragically out of reach.

The Nutrition & Health Education Programme exists to close that gap. We work directly with pregnant women, lactating mothers, and women with young children, teaching them practical, affordable nutrition principles that protect their babies during the most critical windows of development.

Our workshops cover healthy eating during pregnancy, the importance of exclusive breastfeeding, and how to introduce a balanced diet to children from six months to two years. We also run psychosocial workshops that address the emotional and mental health dimension of motherhood in poverty — because a mother's mental health is inseparable from her child's wellbeing.

The results extend far beyond individual families. When mothers understand nutrition, childhood illness decreases, stunting rates fall, and school readiness improves. One session with a mother ripples out into a generation.`,
    whatIsIncluded: [
      "Workshops for pregnant and lactating mothers and women with young children",
      "Guidance on healthy pregnancy nutrition and infant feeding",
      "Infant and young child feeding education (6–24 months)",
      "Psychosocial workshops for mothers and caregivers",
      "Balanced diet planning using locally available foods",
      "Ongoing support groups for new mothers",
    ],
    impactStatement:
      "Hundreds of mothers equipped with nutrition and psychosocial knowledge to raise healthier children",
    donationNudge:
      "A nutrition workshop costs R1,200 — your contribution makes it possible.",
    image: "/images/nutrition-edu-01.jpg",
    heroImage: "/images/nutrition-edu-01.jpg",
  },
  {
    id: "homecare",
    name: "Home-Based Care",
    slug: "home-based-care",
    category: "nutrition",
    pillar: "Nutrition & Health",
    shortDescription:
      "Trained caregivers visit people living with HIV/AIDS who are too ill to care for themselves — bringing food hampers, first aid, and dignity directly to their homes.",
    fullDescription: `In communities where HIV/AIDS still carries stigma and healthcare is not easily accessible, the most vulnerable people can fall through every safety net. They are ill. They are alone. And without help, they cannot manage even the most basic tasks of daily life.

Our Home-Based Care Programme sends trained caregivers directly into homes. These caregivers are not just healthcare workers — they are companions, advocates, and lifelines.

During each visit, they provide hands-on care, emotional support, and first aid. They bring food hampers so families have enough to eat. They supply hygiene products to preserve dignity. They monitor the health of the individual and escalate to medical facilities when needed.

The care they provide is deeply human. It says: you matter. You are not forgotten. Someone will come.`,
    whatIsIncluded: [
      "Regular home visits by trained caregivers",
      "Hands-on personal care and first aid",
      "Emotional support and companionship",
      "Food hamper delivery to food-insecure households",
      "Hygiene product supply and personal care",
      "Medical referrals and health monitoring",
    ],
    impactStatement:
      "Caregivers actively serving people living with HIV/AIDS across our communities",
    donationNudge:
      "A food hamper costs R950 and feeds a family through the month.",
    image: "/images/home-04.jpg",
    heroImage: "/images/home-04.jpg",
  },

  // ── PILLAR 2: EDUCATION ───────────────────────────────────────────────────
  {
    id: "school",
    name: "Literacy & Saturday School",
    slug: "school-sponsorship",
    category: "education",
    pillar: "Education",
    shortDescription:
      "We sponsor school fees, uniforms, and stationery — and run Saturday school and a literacy programme to keep children learning and advancing.",
    fullDescription: `Education is the single most powerful tool a child can be given. But in the communities we serve, school fees are a barrier that keeps thousands of children out of classrooms every year. When a family must choose between food and fees, food wins — and a child's future is quietly closed.

The Literacy & Saturday School Programme removes that barrier. We pay school fees directly. We buy uniforms so children are not turned away at the school gate. We supply stationery so they can write, draw, and learn without shame.

Our Saturday School programme provides additional academic support for children who are falling behind, or who have gaps in their learning due to periods of non-attendance. Our Literacy Programme targets the specific challenge of reading — because a child who cannot read cannot access any other education.`,
    whatIsIncluded: [
      "Direct payment of school fees for sponsored children",
      "School uniforms and stationery provision",
      "Saturday school for additional academic support",
      "Dedicated literacy programme for struggling readers",
      "Monitoring of academic progress and school attendance",
    ],
    impactStatement:
      "Children sponsored through school, with lives transformed by access to education",
    donationNudge: "R500 covers a full month of school fees for one child.",
    image: "/images/literacy-01.jpg",
    heroImage: "/images/literacy-01.jpg",
  },
  {
    id: "ecd",
    name: "Early Childhood Development",
    slug: "early-childhood-development",
    category: "education",
    pillar: "Education",
    shortDescription:
      "Our community preschool prepares children aged 3–6 for Grade 1, giving every child the strongest possible start in life.",
    fullDescription: `The first six years of a child's life are when the brain develops most rapidly. The experiences, stimulation, and care a child receives during this window shape their cognitive, emotional, and social development for life.

The Early Childhood Development (ECD) Programme addresses this gap. Our community preschool welcomes children aged 3 to 6, providing structured, nurturing education that prepares them to thrive when they enter formal schooling.

Our teachers work with small groups, using play-based learning to build numeracy, literacy foundations, language skills, and social development. Children learn to count, to recognise letters, to share, to listen, and to love learning.`,
    whatIsIncluded: [
      "Full preschool programme for children aged 3–6",
      "Play-based learning and structured curriculum",
      "Numeracy and pre-literacy foundations",
      "Language development and social skills",
      "School readiness preparation for Grade 1",
    ],
    impactStatement:
      "Children consistently arriving at Grade 1 ready to learn, thanks to our ECD programme",
    donationNudge: "R600 covers a full term of preschool for one child.",
    image: "/images/literacy-03.jpg",
    heroImage: "/images/literacy-03.jpg",
  },
  {
    id: "orphans",
    name: "Orphans & Vulnerable Children",
    slug: "orphans-and-vulnerable-children",
    category: "education",
    pillar: "Education",
    shortDescription:
      "We provide food support, clothing, psychosocial counselling, and a sense of belonging to the most vulnerable children in our communities.",
    fullDescription: `The communities we serve have some of the highest rates of orphaned children in the region — the legacy of HIV/AIDS, poverty, and instability. Grandmothers raise grandchildren alone. Older siblings parent younger ones. Children grow up without the stability, love, and material basics that every child deserves.

The Orphans & Vulnerable Children (OVC) Programme exists to see these children — to say their names, to meet their needs, and to surround them with the support that poverty has denied them.

We provide clothing so children are warm and can attend school without shame. We support families with food hampers to ensure children are fed. We offer psychosocial counselling to help children process loss, trauma, and the particular pressures of growing up in poverty. And through our housing programme, we have built homes for widows and grandmothers raising orphaned children in unsafe shelters.`,
    whatIsIncluded: [
      "Food hamper support for families of orphaned and vulnerable children",
      "Clothing and shoes for children in need",
      "Psychosocial counselling and emotional support",
      "Housing construction for widows and grandmothers",
      "Integration with feeding and education programmes",
    ],
    impactStatement:
      "Orphaned and vulnerable children supported with food, clothing, counselling and housing across our communities",
    donationNudge:
      "R1,000 provides support with food hampers for a family of five for a full month.",
    image: "/images/smile-05.jpg",
    heroImage: "/images/smile-05.jpg",
  },

  // ── PILLAR 3: SKILLS DEVELOPMENT ─────────────────────────────────────────
  {
    id: "skills",
    name: "Skills Development & Training",
    slug: "skills-training",
    category: "empowerment",
    pillar: "Skills Development & Training",
    shortDescription:
      "We run short courses and women's and youth empowerment training, equipping community members with practical skills and economic independence.",
    fullDescription: `Receiving care is not the same as having agency. The Skills Development & Training Programme was built on the belief that people in our communities deserve not just support, but the tools to build economic independence.

Our programme offers short courses in arts, crafts, sewing, and other practical disciplines, alongside dedicated women's empowerment training and youth empowerment programmes. The focus is on producing quality, sellable goods and building the confidence and capability to earn.

Items crafted by our participants are sold through bulungu.co.za, an online store that connects their work to buyers who want to make a meaningful purchase. The income generated goes directly back to the individual.

For many participants, it is the first time they have earned their own money. The psychological impact of that — of creating something, selling it, and holding the earnings — is transformational.`,
    whatIsIncluded: [
      "Short courses in arts, crafts, sewing and practical skills",
      "Women's empowerment training programme",
      "Youth empowerment programme",
      "Access to materials and production equipment",
      "Sales channel through bulungu.co.za online store",
    ],
    impactStatement:
      "Community members empowered with income-generating skills and economic independence",
    donationNudge:
      "R550 covers a full set of training materials for one participant.",
    image: "/images/literacy-02.jpg",
    heroImage: "/images/literacy-02.jpg",
  },

  // ── PILLAR 4: CLIMATE CHANGE & ENVIRONMENT ───────────────────────────────
  {
    id: "climate",
    name: "Sustainable Farming & Climate Change",
    slug: "sustainable-farming",
    category: "environment",
    pillar: "Climate Change & the Environment",
    shortDescription:
      "We are building a training centre in Lunga to equip communities with sustainable farming practices and climate resilience for a changing environment.",
    fullDescription: `Food security and climate change are inseparable. As weather patterns shift, small-scale farmers and vulnerable communities across Southern Africa face increasingly unpredictable rains, soil degradation, and harvest failures. For families already on the edge, a single failed harvest is a crisis.

We are in the process of building a training centre in Lunga, dedicated entirely to sustainable farming practices and climate change education. This will be a place where community members come to learn — not for a single workshop, but as an ongoing centre of knowledge they can return to season after season.

At the Lunga centre, participants will learn practical sustainable farming methods — composting, water conservation, crop rotation, and the cultivation of nutritious vegetables using minimal resources. Alongside this, climate change education will help communities understand, anticipate, and respond to the environmental shifts already affecting their harvests and their food security.

When a family learns to grow their own vegetables and adapt to a changing climate, their nutrition improves, their food costs fall, and their resilience to external shocks increases. A centre built is a community equipped — for this season, and for every season after.`,
    whatIsIncluded: [
      "A dedicated training centre under construction in Lunga",
      "Sustainable farming methods training (composting, water conservation, crop rotation)",
      "Climate change education and resilience workshops",
      "Vegetable garden establishment for households and community plots",
      "Seed and input support for new growers",
      "Ongoing mentorship for farming participants",
    ],
    impactStatement:
      "A new training centre underway in Lunga, equipping communities with sustainable farming and climate resilience skills",
    donationNudge:
      "Your donation helps us build the Lunga centre and equip communities to farm sustainably.",
    image: "/images/lunga-development-01.jpg",
    heroImage: "/images/lunga-development-01.jpg",
  },
];

export const getProgrammeBySlug = (slug) =>
  programmes.find((p) => p.slug === slug);
export const getProgrammeById = (id) => programmes.find((p) => p.id === id);

export const pillars = [
  {
    id: "nutrition",
    title: "Nutrition & Health",
    programmes: [
      "Feeding Programme",
      "Nutrition & Health Education",
      "Home-Based Care",
    ],
  },
  {
    id: "education",
    title: "Education",
    programmes: [
      "Literacy & Saturday School",
      "Early Childhood Development",
      "Orphans & Vulnerable Children",
    ],
  },
  {
    id: "skills",
    title: "Skills Development & Training",
    programmes: [
      "Short Courses",
      "Women's Empowerment Training",
      "Youth Empowerment",
    ],
  },
  {
    id: "climate",
    title: "Climate Change & the Environment",
    programmes: ["Sustainable Farming", "Climate Change Education"],
  },
];
